import React, { useEffect, useState } from "react";
import {
  Modal,
  Tabs,
  Form,
  Input,
  Button,
  Descriptions,
  Skeleton,
  message,
} from "antd";
import { useTranslation } from "react-i18next";
import accountsApi from "../api/Accounts/AccountsApi";

interface Props {
  open: boolean;
  onClose: () => void;
}

const MyProfileModal: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const data = await accountsApi.me();
        setProfile(data);
        profileForm.setFieldsValue({
          first_name: data.first_name,
          last_name: data.last_name,
          company: data.company,
        });
      } catch (e) {
        message.error(t("Failed to load profile"));
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const handleProfileSave = async (values: any) => {
    setSavingProfile(true);
    try {
      const updated = await accountsApi.updateMe(values);
      setProfile(updated);
      const stored = localStorage.getItem("userInfo");
      if (stored) {
        const userInfo = JSON.parse(stored);
        userInfo.name = `${updated.first_name} ${updated.last_name}`.trim();
        localStorage.setItem("userInfo", JSON.stringify(userInfo));
      }
      message.success(t("Profile updated"));
    } catch (e) {
      message.error(t("Failed to update profile"));
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSave = async (values: any) => {
    if (values.new_password !== values.confirm_password) {
      message.error(t("Passwords do not match"));
      return;
    }
    setSavingPassword(true);
    try {
      await accountsApi.changePassword(
        values.current_password,
        values.new_password
      );
      message.success(t("Password updated successfully"));
      passwordForm.resetFields();
      onClose();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      message.error(detail || t("Failed to change password"));
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t("My Profile")}
      onCancel={onClose}
      footer={null}
      width={640}
      destroyOnClose
    >
      {loading ? (
        <Skeleton active />
      ) : (
        <Tabs
          items={[
            {
              key: "info",
              label: t("Profile information"),
              children: (
                <>
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label={t("Email")}>
                      {profile?.email}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("Full name")}>
                      {profile?.name}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("Status")}>
                      {profile?.status}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("Verified")}>
                      {profile?.isVerified ? t("Yes") : t("No")}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("Groups")}>
                      {profile?.groups
                        ?.map((g: any) => g.name)
                        .join(", ") || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("Last login")}>
                      {profile?.last_login
                        ? new Date(profile.last_login).toLocaleString()
                        : "-"}
                    </Descriptions.Item>
                  </Descriptions>
                  <Form
                    form={profileForm}
                    layout="vertical"
                    style={{ marginTop: 16 }}
                    onFinish={handleProfileSave}
                  >
                    <Form.Item
                      name="first_name"
                      label={t("First name")}
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="last_name"
                      label={t("Last name")}
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item name="company" label={t("Company")}>
                      <Input />
                    </Form.Item>
                    <Form.Item>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={savingProfile}
                      >
                        {t("Save")}
                      </Button>
                    </Form.Item>
                  </Form>
                </>
              ),
            },
            {
              key: "password",
              label: t("Change password"),
              children: (
                <Form
                  form={passwordForm}
                  layout="vertical"
                  onFinish={handlePasswordSave}
                >
                  <Form.Item
                    name="current_password"
                    label={t("Current password")}
                    rules={[{ required: true }]}
                  >
                    <Input.Password autoComplete="current-password" />
                  </Form.Item>
                  <Form.Item
                    name="new_password"
                    label={t("New password")}
                    rules={[
                      { required: true },
                      { min: 6, message: t("Minimum 6 characters") },
                    ]}
                  >
                    <Input.Password autoComplete="new-password" />
                  </Form.Item>
                  <Form.Item
                    name="confirm_password"
                    label={t("Confirm new password")}
                    dependencies={["new_password"]}
                    rules={[
                      { required: true },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("new_password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error(t("Passwords do not match"))
                          );
                        },
                      }),
                    ]}
                  >
                    <Input.Password autoComplete="new-password" />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={savingPassword}
                    >
                      {t("Update password")}
                    </Button>
                  </Form.Item>
                </Form>
              ),
            },
          ]}
        />
      )}
    </Modal>
  );
};

export default MyProfileModal;
